import { useContext, useEffect, useState } from 'react';
import { HeatmapChart, HeatmapDataPoint, HeatmapDataSeries } from '@ep/echarts';
import { GridComponentOption, XAXisComponentOption, YAXisComponentOption } from 'echarts';
import { Grid, Theme, useTheme } from '@mui/material';
import {
  Country,
  Division,
  MetricRecord,
  Region,
  ReportingPeriod,
  ReportingPeriodGroup,
  Site,
  Subdivision
} from '@esg/esg-global-types';
import { MetricRecordFilters } from '@esg/syntiro-api-core';
import {
  MetricRecordDataLevel,
  MetricRecordEntitiesMap,
  MetricRecordReportingEntity,
  getMetricRecords,
  metric_record_data_levels
} from '../../../graphql/services/metric_record.service';
import { values } from '../../../util/general_utils';
import { MetricExtended, metric_label } from '../../../lib/metric_capture/metric';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { MetadataError } from '@ep/error-handling';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import PrimarySkeleton from '../../shared/loading/PrimarySkeleton';
import { reportingPeriodFilterByGroup } from '../../../lib/metric_capture/reporting_period';
import MetricRecordDataLevelSelect from '../../shared/input/select/MetricRecordDataLevelSelect';
import CountrySelect from '../../shared/input/select/CountrySelect';
import RegionSelect from '../../shared/input/select/RegionSelect';
import DivisionSelect from '../../shared/input/select/DivisionSelect';
import SubdivisionSelect from '../../shared/input/select/SubdivisionSelect';
import SiteSelect from '../../shared/input/select/SiteSelect';
import MetricSelect from '../../shared/input/select/MetricSelect';
import {
  FUNCTION_UNKNOWN_FAILURE,
  COMPONENT_UNKNOWN_FAILURE,
  FEEDBACK_ACTION_FAILURE,
  FEEDBACK_UNKNOWN_FAILURE
} from '../../../util/global_variables';
import { GET_METRIC_RECORDS_REPORTING_LEVELS_ONLY } from '../../../graphql/queries/metric_record.queries';

interface QuantitativeDataCompletenessHeatmapProps {
  reporting_period_group: ReportingPeriodGroup;
  entities_map: MetricRecordEntitiesMap;
}

interface QuantitativeMetricRecordDataCompletenessFilters {
  data_level: MetricRecordDataLevel | null;
  level_entity: MetricRecordReportingEntity | null;
  metrics: Array<MetricExtended>;
}

const SOURCE = 'QuantitativeDataCompletenessHeatmap.tsx';

/**
 * Heatmap chart displaying the captured metric record progress for quantitative metrics.
 * @param {ReportingPeriodGroup} reporting_period_group Reporting period group to fetch metric record data for.
 * @param {MetricRecordEntitiesMap} entities_map Metric record reference entity data to use for chart and filtering.
 * @returns {JSX.Element}
 */
const QuantitativeDataCompletenessHeatmap = ({
  reporting_period_group,
  entities_map
}: QuantitativeDataCompletenessHeatmapProps) => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);
  const [quantitativeMetricRecords, setQuantitativeMetricRecords] = useState<Array<MetricRecord>>(
    []
  );
  const [metricRecordFilters, setMetricRecordFilters] =
    useState<QuantitativeMetricRecordDataCompletenessFilters>({
      data_level: null,
      level_entity: null,
      metrics: []
    });
  const [metricRecordsLoading, setMetricRecordsLoading] = useState<boolean>(true);
  const theme: Theme = useTheme();

  // List data levels available for the user to filter on.
  const available_data_levels: Array<MetricRecordDataLevel> = metric_record_data_levels.filter(
    (data_level: MetricRecordDataLevel) =>
      Boolean(
        (data_level === 'national' && values(entities_map.countries).length) ||
        (data_level === 'regional' && values(entities_map.regions).length) ||
        (data_level === 'divisional' && values(entities_map.divisions).length) ||
        (data_level === 'subdivisional' && values(entities_map.subdivisions).length) ||
        (data_level === 'site' && values(entities_map.sites).length)
      )
  );
  const reporting_periods = values<ReportingPeriod>(entities_map.reporting_periods);
  const reporting_periods_for_group: Array<ReportingPeriod> = reportingPeriodFilterByGroup(
    reporting_period_group.id,
    reporting_periods,
    true
  );
  const quantitative_metrics: Array<MetricExtended> = values<MetricExtended>(entities_map.metrics)
    .filter(
      (metric: MetricExtended) =>
        'is_quantitative' in metric.standard && metric.standard.is_quantitative
    )
    .sort((a: MetricExtended, b: MetricExtended) => b.name.localeCompare(a.name));
  const filtered_metric_ids: Array<string> = metricRecordFilters.metrics.map(
    (metric: MetricExtended) => metric.id
  );
  const filtered_metrics: Array<MetricExtended> = quantitative_metrics.filter(
    (metric: MetricExtended) => {
      return Boolean(filtered_metric_ids.length === 0 || filtered_metric_ids.includes(metric.id));
    }
  );
  const reporting_period_names: Array<string> = reporting_periods_for_group.map(
    (reporting_period: ReportingPeriod) => reporting_period.name
  );
  const metric_names: Array<string> = filtered_metrics.map((metric: MetricExtended) => metric.name);

  const handleChangeMetricRecordFilterMetrics = (
    metrics: Array<MetricExtended> | MetricExtended | null
  ) => {
    if (Array.isArray(metrics)) {
      setMetricRecordFilters({
        ...metricRecordFilters,
        metrics: metrics
      });
    }
  };

  const handleChangeMetricRecordFilterDataLevel = (
    data_level: Array<MetricRecordDataLevel> | MetricRecordDataLevel | null
  ) => {
    if (!Array.isArray(data_level)) {
      setMetricRecordFilters({
        data_level: data_level,
        level_entity: null,
        metrics: metricRecordFilters.metrics
      });
    }
  };

  const handleChangeMetricRecordFilterEntity = (
    level_entity: MetricRecordReportingEntity | null
  ) => {
    setMetricRecordFilters({
      ...metricRecordFilters,
      level_entity: level_entity
    });
  };

  // Fetch quantitative metric record data for given reporting period group.
  const fetchMetricRecords = async (): Promise<void> => {
    try {
      setMetricRecordsLoading(true);
      if (!group || !company || !reporting_period_group) {
        throw new Error('Cannot fetch Metric Records without required parameters');
      }
      const reporting_period_ids: Array<string> = reporting_periods_for_group.map(
        (reporting_period: ReportingPeriod) => reporting_period.id
      );
      const metric_record_filters: MetricRecordFilters = {
        reporting_period_ids: reporting_period_ids
      };
      const quantitative_metric_records: Array<MetricRecord> = await getMetricRecords(
        { group_id: group.id, company_id: company.id },
        metric_record_filters,
        GET_METRIC_RECORDS_REPORTING_LEVELS_ONLY
      );
      setQuantitativeMetricRecords(quantitative_metric_records);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'fetchMetricRecords'),
          {
            group: group,
            company: company,
            reporting_period_group: reporting_period_group
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to fetch metric records', tracking_id),
        state: true,
        type: 'error'
      });
    } finally {
      setMetricRecordsLoading(false);
    }
  };

  // Transform quantitative metric records to heatmap data points.
  const processMetricRecordsToHeatmapData = (): Array<HeatmapDataPoint> => {
    if (quantitativeMetricRecords.length === 0) return [];

    // Filter metric records based on current data level and entity filter inputs.
    const filtered_metric_records: Array<MetricRecord> = quantitativeMetricRecords.filter(
      (metric_record: MetricRecord) => {
        if (!metricRecordFilters.data_level) {
          return true;
        }
        const level_entity: MetricRecordReportingEntity | null = metricRecordFilters.level_entity;

        // Filter based on the selected data level and entity.
        switch (metricRecordFilters.data_level) {
          case 'national':
            return Boolean(
              metric_record.country &&
              !metric_record.region &&
              (!level_entity || metric_record.country.id === level_entity.id)
            );
          case 'regional':
            return Boolean(
              metric_record.region &&
              !metric_record.site &&
              (!level_entity || metric_record.region.id === level_entity.id)
            );
          case 'divisional':
            return Boolean(
              metric_record.division &&
              !metric_record.subdivision &&
              (!level_entity || metric_record.division.id === level_entity.id)
            );
          case 'subdivisional':
            return Boolean(
              metric_record.subdivision &&
              !metric_record.site &&
              (!level_entity || metric_record.subdivision.id === level_entity.id)
            );
          case 'site':
            return Boolean(
              metric_record.site && (!level_entity || metric_record.site.id === level_entity.id)
            );
          default:
            return true;
        }
      }
    );

    // Create lookup maps for filtered metric and reporting period ids by chart axis indexes.
    const metric_ids_map = new Map<string, number>();
    const reporting_period_ids_map = new Map<string, number>();
    filtered_metrics.forEach((metric: MetricExtended, index: number) => {
      metric_ids_map.set(metric.id, index);
    });
    reporting_periods_for_group.forEach((period, index) => {
      reporting_period_ids_map.set(period.id, index);
    });

    // Lookup map for filtered metric record count by reporting period index (x axis) and metric index (y axis).
    const metric_record_counts_map = new Map<string, number>();

    filtered_metric_records.forEach((metric_record: MetricRecord) => {
      const metric_index: number | undefined = metric_ids_map.get(metric_record.metric.id);
      const reporting_period_index: number | undefined = reporting_period_ids_map.get(
        metric_record.reporting_period?.id ?? ''
      );

      if (metric_index !== undefined && reporting_period_index !== undefined) {
        const axis_index_key = `${reporting_period_index}-${metric_index}`;
        const existing_count: number | undefined = metric_record_counts_map.get(axis_index_key);
        metric_record_counts_map.set(axis_index_key, (existing_count ?? 0) + 1);
      }
    });

    // Convert counts map to heatmap data points.
    const heatmap_data_points: Array<HeatmapDataPoint> = [];
    metric_record_counts_map.forEach((metric_record_count: number, key: string) => {
      const [reporting_period_index, metric_index] = key
        .split('-')
        .map((index: string) => Number(index));
      heatmap_data_points.push([reporting_period_index, metric_index, metric_record_count]);
    });

    return heatmap_data_points;
  };

  useEffect(() => {
    try {
      fetchMetricRecords();
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : COMPONENT_UNKNOWN_FAILURE('QuantitativeDataCompletenessHeatmap'),
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_UNKNOWN_FAILURE(tracking_id),
        state: true,
        type: 'error'
      });
    }
  }, [reporting_period_group]);

  // Setup prop values for imported chart.
  const grid_options: GridComponentOption = {
    height: '100%',
    top: '0%',
    left: '0%',
    right: '2%',
    containLabel: true
  };
  const x_axis_options: XAXisComponentOption = {
    type: 'category',
    data: reporting_period_names,
    splitArea: {
      show: true
    }
  };
  const y_axis_options: YAXisComponentOption = {
    type: 'category',
    data: metric_names,
    splitArea: {
      show: true
    }
  };
  const metric_record_count_data_points: Array<HeatmapDataPoint> =
    processMetricRecordsToHeatmapData();
  const series: Array<HeatmapDataSeries> = [
    {
      name: 'Metric Records',
      type: 'heatmap',
      data: metric_record_count_data_points,
      label: {
        show: true
      },
      emphasis: {
        itemStyle: {
          shadowBlur: 10,
          shadowColor: 'rgba(0, 0, 0, 0.5)'
        }
      }
    }
  ];
  const chart_height: number = Math.max(400, filtered_metrics.length * 25 + 150);
  const color_range: Array<string> = [
    theme.palette.teal.light,
    theme.palette.teal.main,
    theme.palette.teal.dark
  ];

  return metricRecordsLoading ? (
    <PrimarySkeleton variant="rounded" height={500} />
  ) : (
    <Grid container spacing={4}>
      <Grid size={3}>
        <MetricSelect
          options={quantitative_metrics}
          selected_options={metricRecordFilters.metrics}
          group_by="metric_group.name"
          multi_select
          input_label={metric_label.many}
          disable_selected_tags
          handleChangeMetrics={handleChangeMetricRecordFilterMetrics}
        />
      </Grid>
      <Grid size={3}>
        <MetricRecordDataLevelSelect
          options={available_data_levels}
          selected_options={metricRecordFilters.data_level}
          handleChangeMetricRecordDataLevel={handleChangeMetricRecordFilterDataLevel}
        />
      </Grid>
      {metricRecordFilters.data_level && (
        <Grid size={3}>
          {(() => {
            const level_entity: MetricRecordReportingEntity | null =
              metricRecordFilters.level_entity;
            switch (metricRecordFilters.data_level) {
              case 'national':
                return (
                  <CountrySelect
                    options={values<Country>(entities_map.countries)}
                    selected_options={level_entity && 'code' in level_entity ? level_entity : null}
                    hide_icons
                    variant="standard"
                    handleChangeCountries={(country: Country | Array<Country> | null) => {
                      if (!Array.isArray(country)) {
                        handleChangeMetricRecordFilterEntity(country);
                      }
                    }}
                  />
                );
              case 'regional':
                return (
                  <RegionSelect
                    options={values<Region>(entities_map.regions)}
                    selected_options={
                      level_entity && 'country' in level_entity ? level_entity : null
                    }
                    group_by="country.name"
                    handleChangeRegions={(region: Region | Array<Region> | null) => {
                      if (!Array.isArray(region)) {
                        handleChangeMetricRecordFilterEntity(region);
                      }
                    }}
                  />
                );
              case 'divisional':
                return (
                  <DivisionSelect
                    options={values<Division>(entities_map.divisions)}
                    selected_options={level_entity}
                    handleChangeDivisions={(division: Division | Array<Division> | null) => {
                      if (!Array.isArray(division)) {
                        handleChangeMetricRecordFilterEntity(division);
                      }
                    }}
                  />
                );
              case 'subdivisional':
                return (
                  <SubdivisionSelect
                    options={values<Subdivision>(entities_map.subdivisions)}
                    selected_options={
                      level_entity && 'division' in level_entity ? level_entity : null
                    }
                    group_by="division.name"
                    handleChangeSubdivisions={(
                      subdivision: Subdivision | Array<Subdivision> | null
                    ) => {
                      if (!Array.isArray(subdivision)) {
                        handleChangeMetricRecordFilterEntity(subdivision);
                      }
                    }}
                  />
                );
              case 'site':
                return (
                  <SiteSelect
                    options={values<Site>(entities_map.sites)}
                    selected_options={
                      level_entity && 'subdivision' in level_entity ? level_entity : null
                    }
                    group_by="subdivision.name"
                    handleChangeSites={(site: Site | Array<Site> | null) => {
                      if (!Array.isArray(site)) {
                        handleChangeMetricRecordFilterEntity(site);
                      }
                    }}
                  />
                );
              default:
                return <></>;
            }
          })()}
        </Grid>
      )}
      <Grid size={12} sx={{ my: 2 }}>
        <HeatmapChart
          grid_options={grid_options}
          x_axis_options={x_axis_options}
          y_axis_options={y_axis_options}
          series={series}
          color_range={color_range}
          width={'100%'}
          height={chart_height}
        />
      </Grid>
    </Grid>
  );
};

export default QuantitativeDataCompletenessHeatmap;
